import type { Matrix2D, ObjectId, SpatialObject } from '@/types';
import { IDENTITY_MATRIX, multiply, objectMatrix } from '@/core/geometry/transform';

/**
 * Local→world matrix computed straight from an object record, without a scene
 * graph. Used by React views so that a moved parent re-renders its children
 * (the selector returns a string, so unchanged matrices cost nothing).
 */
export const worldMatrixFromRecord = (
  objects: Readonly<Record<ObjectId, SpatialObject>>,
  id: ObjectId,
): Matrix2D | undefined => {
  const chain: SpatialObject[] = [];
  const guard = new Set<ObjectId>();
  let current: ObjectId | null = id;
  while (current !== null && !guard.has(current)) {
    guard.add(current);
    const obj: SpatialObject | undefined = objects[current];
    if (!obj) break;
    chain.push(obj);
    current = obj.parentId;
  }
  if (chain.length === 0) return undefined;
  return chain.reduceRight((m, node) => multiply(m, objectMatrix(node.transform, node.size)), IDENTITY_MATRIX);
};
